import { Icon, Stack, StackProps, Tooltip, Typography } from '@mui/material';
import {
  DATE_TIME_FORMAT_FNS,
  JustificationBase,
  JustificationSave,
  JustificationStatus,
  WithName,
} from '@schooly/api';
import {
  Bubble,
  EyeCloseIcon,
  NewTabIcon,
  ParentIcon,
  SimpleButton,
  StaffIcon,
} from '@schooly/style';
import { getUserFullName } from '@schooly/utils/user-helpers';
import { format } from 'date-fns';
import { FC, ReactElement, ReactNode, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { JustificationLabel } from './JustificationLabel';
import { getJustificationStatusPalette } from './utils';

export type PendingJustificationNote = JustificationSave & { creator: WithName };
export type JustificationEmptyTextIds = Partial<Record<JustificationStatus, string>>;

const DEFAULT_EMPTY_TEXT_IDS: JustificationEmptyTextIds = {
  [JustificationStatus.Pending]: 'attendance-justification-HereWillAppearJustification',
  [JustificationStatus.Expired]: 'attendance-justification-WeekHasPassed',
  [JustificationStatus.Closed]: 'attendance-justification-AutomaticallyClosed',
};

type JustificationContentProps = {
  justification: JustificationBase;
  pendingNote?: PendingJustificationNote;
  relationId?: string;
  canViewStudents?: boolean;
  emptyTextIds?: JustificationEmptyTextIds;
  sx?: StackProps['sx'];
};

export const JustificationContent: FC<JustificationContentProps> = ({
  justification,
  pendingNote,
  relationId,
  canViewStudents,
  emptyTextIds,
  sx,
}) => {
  const { formatMessage } = useIntl();
  const { status, parent_submissions, staff_reasons, staff_requests } = justification;

  const palette = getJustificationStatusPalette(status);
  const label = formatMessage({ id: `attendance-justification-${status}` });

  const items = useMemo(() => {
    const parentIcon = (
      <Icon>
        <ParentIcon />
      </Icon>
    );
    const staffIcon = (
      <Icon>
        <StaffIcon />
      </Icon>
    );
    const visibilityIcon = (
      <Tooltip
        title={formatMessage({ id: 'attendance-justification-VisibleToSchoolOnly' })}
        componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
      >
        <Icon fontSize="small" sx={{ color: 'primary.main' }}>
          <EyeCloseIcon />
        </Icon>
      </Tooltip>
    );

    const submissions = parent_submissions.map((item) => ({
      date: item.submitted_at,
      text: item.text,
      creatorName: item.submitted_by ? getUserFullName(item.submitted_by) : undefined,
      creatorIcon: parentIcon,
    }));
    const reasons = staff_reasons.map((item) => ({
      date: item.created_at,
      text: item.text,
      creatorName: item.creator ? getUserFullName(item.creator) : undefined,
      creatorIcon: staffIcon,
      dateIcon: visibilityIcon,
    }));
    const requests = staff_requests.map((item) => ({
      date: item.created_at,
      text: item.text,
      creatorName: item.creator ? getUserFullName(item.creator) : undefined,
      creatorIcon: staffIcon,
    }));

    const saved: TimelineItem[] = [...submissions, ...reasons, ...requests]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(({ date, ...item }) => ({
        ...item,
        key: date,
        label: format(new Date(date), DATE_TIME_FORMAT_FNS),
      }));

    if (!pendingNote) return saved;

    return [
      ...saved,
      {
        key: 'pending',
        label: formatMessage({ id: 'attendance-justification-WillBeSentOnSave' }),
        text:
          'staff_reason' in pendingNote
            ? pendingNote.staff_reason.text
            : pendingNote.staff_request.text,
        creatorName: getUserFullName(pendingNote.creator),
        creatorIcon: staffIcon,
        dateIcon: 'staff_reason' in pendingNote ? visibilityIcon : undefined,
      },
    ];
  }, [formatMessage, parent_submissions, staff_reasons, staff_requests, pendingNote]);

  const emptyTextId = { ...DEFAULT_EMPTY_TEXT_IDS, ...emptyTextIds }[status];

  const renderEmptyContent = () => {
    if (!emptyTextId) return null;

    const text = <Typography color="primary.main">{formatMessage({ id: emptyTextId })}</Typography>;

    if (status === JustificationStatus.Expired) {
      return (
        <>
          {text}
          {canViewStudents && relationId && (
            <Link to={{ pathname: `/students/${relationId}`, hash: '#family' }} target="_blank">
              <SimpleButton startIcon={<NewTabIcon />} size="small">
                {formatMessage({ id: 'attendance-justification-ContactFamily' })}
              </SimpleButton>
            </Link>
          )}
        </>
      );
    }

    return text;
  };

  return (
    <Stack gap={1} alignItems="flex-start">
      <JustificationLabel palette={palette} label={label} icon={null} />
      {items.length ? (
        <Stack gap={1} maxHeight={200} overflow="auto" width="100%" sx={sx}>
          {items.map((item) => (
            <JustificationTimelineItem key={item.key} item={item} />
          ))}
        </Stack>
      ) : (
        renderEmptyContent()
      )}
    </Stack>
  );
};

type TimelineItem = {
  key: string;
  label: string;
  text: string;
  creatorName?: string;
  creatorIcon: ReactElement;
  dateIcon?: ReactNode;
};

const JustificationTimelineItem: FC<{ item: TimelineItem }> = ({ item }) => (
  <Stack gap={1} alignItems="flex-start">
    <Stack direction="row" gap={0.5} alignItems="center">
      <Typography variant="h4" color="text.secondary">
        {item.label}
      </Typography>
      {item.dateIcon}
    </Stack>
    <Typography color="primary.main">{item.text}</Typography>
    {item.creatorName && (
      <Bubble
        tone="gray-inverse"
        disableHover
        label={item.creatorName}
        icon={item.creatorIcon}
        sx={{
          '& .MuiChip-icon': { color: 'primary.main' },
          '& .MuiChip-label': { pl: 0.5, py: 0 },
        }}
      />
    )}
  </Stack>
);
